import { yupResolver } from "@hookform/resolvers/yup";
import CustomModal from "../components/modal";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useContext, useRef } from "react";
import ErrorManagement from "../components/errormanagement";
import { UserDataContext } from "../context/UserContextProvider";
import { ToastDataContext } from "../context/ToastContextProvider";
import { hrCreateEmployeeEndpoint } from "../hr_endpoints";
import CustomTextInput, { textInputClassName } from "../components/textInput";
import CustomPasswordInput from "../components/passwordInput";
import PasswordRules, { passwordRule } from "../components/passwordRules";
import { classNames } from "../App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import CustomSelect from "../components/select";
export default function HREmployeeAddModal({ QRComponent, open, setOpen, forceMutate }) {

    const { t } = useTranslation();

    const userContext = useContext(UserDataContext)
    const toastContext = useContext(ToastDataContext)

    const schema = yup.object({
        first_name: yup.string().required(t("employees.addEmployeeModal.firstNameRequired")),
        last_name: yup.string().required(t("employees.addEmployeeModal.lastNameRequired")),
        email: yup.string().email(t("employees.addEmployeeModal.emailInvalid")).required(t("employees.addEmployeeModal.emailRequired")),
        phone_number: yup.string().optional().nullable(),
        password: yup.string().required(t("employees.addEmployeeModal.passwordRequired"))
            .test(...passwordRule("length", t("employees.addEmployeeModal.passwordMinLength")))
            .test(...passwordRule("uppercase", t("employees.addEmployeeModal.passwordUppercase")))
            .test(...passwordRule("special", t("employees.addEmployeeModal.passwordSpecialCharacter")))
    })

    const formRef = useRef(null);
    const navigate = useNavigate()

    const onSubmit = async (data) => {
        const response = await fetch(hrCreateEmployeeEndpoint, {
            method: "POST",
            body: JSON.stringify({ ...data, lang }),
            headers: {
                "Authorization": `Bearer ${userContext.user?.token}`,
                "Content-Type": "application/json"
            }
        })

        if (response.status === 401) {
            userContext.setUserData({})
            navigate('/login')
            toastContext.error(t("sessionExpired"));
        }

        if (response.ok) {
            toastContext.success(t("employees.addEmployeeModal.employeeAdded"))
            forceMutate()
        } else {
            toastContext.error(await response.text())
        }
        setOpen(false)
        reset()
    }

    const onCancel = () => {
        setOpen(false);
    }

    const { register, handleSubmit, watch, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema)
    });

    // The rule checklist fills in while the password is typed, so HR is not
    // told the password is wrong only after submitting the whole employee.
    const passwordValue = watch('password', '')

    const langOptions = [
        { id: 'ro', name: t('settings.ro') },
        { id: 'en', name: t('settings.en') },
    ]

    const [lang, setLang] = useState('ro');

    return (
        <CustomModal open={open} setOpen={setOpen} title={t("employees.addEmployeeModal.addEmployee")} cancelButton={t("employees.addEmployeeModal.close")} confirmButton={t("employees.addEmployeeModal.add")} highWidthTable={true} onConfirm={() => {
            formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }} onCancel={onCancel} noOutsideClick={true} withIcon={false}>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <QRComponent />
                </div>
                <div>
                    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                        <div className="flex flex-row gap-2 flex-wrap">
                            <div>
                                <label htmlFor="first_name" className="block font-subtitle text-neutral-1100 mb-2">{t("employees.addEmployeeModal.firstName")}</label>
                                <CustomTextInput register={register('first_name')} placeholder={t("employees.addEmployeeModal.firstNamePlaceholder")} className="w-[20rem]" />
                                <ErrorManagement errorObj={errors.first_name} />
                            </div>
                            <div>
                                <label htmlFor="last_name" className="block font-subtitle text-neutral-1100 mb-2">{t("employees.addEmployeeModal.lastName")}</label>
                                <CustomTextInput register={register('last_name')} placeholder={t("employees.addEmployeeModal.lastNamePlaceholder")} className="w-[20rem]" />
                                <ErrorManagement errorObj={errors.last_name} />
                            </div>
                            <div>
                                <label htmlFor="email" className="block font-subtitle text-neutral-1100 mb-2">{t("employees.addEmployeeModal.email")}</label>
                                <CustomTextInput register={register('email')} placeholder={t("employees.addEmployeeModal.emailPlaceholder")} className="w-[20rem]" />
                                <ErrorManagement errorObj={errors.email} />
                            </div>
                            <div>
                                <label htmlFor="phone_number" className="block font-subtitle text-neutral-1100 mb-2">{t("employees.addEmployeeModal.phoneNumber")}</label>
                                <CustomTextInput register={register('phone_number')} placeholder={t("employees.addEmployeeModal.phoneNumberPlaceholder")} className="w-[20rem]" />
                                <ErrorManagement errorObj={errors.phone_number} />
                            </div>
                            <div>
                                <label htmlFor="password" className="block font-subtitle text-neutral-1100 mb-2">{t("employees.addEmployeeModal.password")}</label>
                                <CustomPasswordInput
                                    register={register('password')}
                                    placeholder={t("employees.addEmployeeModal.passwordPlaceholder")}
                                    className={classNames(textInputClassName, "w-[20rem]")}
                                    autoComplete="new-password"
                                    showLabel={t('password.show')}
                                    hideLabel={t('password.hide')}
                                    fullWidth={false}
                                />
                                <ErrorManagement errorObj={errors.password} />
                                <PasswordRules
                                    password={passwordValue}
                                    labels={{
                                        length: t('password.ruleLength'),
                                        uppercase: t('password.ruleUppercase'),
                                        special: t('password.ruleSpecial'),
                                    }}
                                />
                            </div>
                            <div>
                                <label htmlFor="lang" className="block font-subtitle text-neutral-1100 mb-2">{t('settings.lang')}</label>
                                <CustomSelect options={langOptions} onChange={(e) => setLang(e.target.value)} value={lang} />
                                <ErrorManagement errorObj={errors.lang} />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </CustomModal>
    )

} 